import './EasySoftwareSearch.scss'

import axios from 'axios'
import { concat, debounce } from 'lodash'
import React, { Dispatch, SetStateAction } from 'react'
import { searchConfig } from 'Root/puxconfig'
import PuxIconBuilder from 'Shared/components/PuxIconBuilder/PuxIconBuilder'
import { t } from 'ttag'

import EasySoftwareSearchResult, {
  EasySoftwareSearchResultItem,
} from './EasySoftwareSearchResult'

interface EasySoftwareSearchResponse {
  status: number
  data: {
    items: [EasySoftwareSearchResultItem]
    searchInformation: {
      totalResults: number
      formattedTotalResults: string
      formattedSearchTime: string
    }
  }
}

interface EasySoftwareSearchProps {
  searchType?: 'header' | 'knowledgebase'
  setLanguageDialogOpen: Dispatch<SetStateAction<boolean>>
}

const LOCALE = process.env.LOCALE ? process.env.LOCALE : `en`
const PROJECT = process.env.PROJECT ? process.env.PROJECT : `EasyRedmineGatsby`

const EasySoftwareSearch: React.FunctionComponent<EasySoftwareSearchProps> = (
  props
) => {
  const [searchQuery, setSearchQuery] = React.useState(``)
  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState<EasySoftwareSearchResultItem[]>([])
  const [showLoadMore, setShowLoadMore] = React.useState(false)
  const [resultsIndex, setResultsIndex] = React.useState(0)
  const [total, setTotal] = React.useState(0)
  const delayedQuery = React.useCallback(
    debounce((query) => getSearchData(query), 500),
    []
  )
  const searchType = props.searchType ?? `header`
  let searchApiURL: string

  switch (searchType) {
    case `knowledgebase`:
      searchApiURL = `${searchConfig[PROJECT].apiUrl}?key=${searchConfig[PROJECT].apiKey}&cx=${searchConfig[PROJECT].localizedOptions[LOCALE].index}&siteSearch=${searchConfig[PROJECT].localizedOptions[LOCALE].knowledgeBaseUrl}&siteSearchFilter=i&q=`
      break
    default:
      searchApiURL = `${searchConfig[PROJECT].apiUrl}?key=${searchConfig[PROJECT].apiKey}&cx=${searchConfig[PROJECT].localizedOptions[LOCALE].index}&q=`
      break
  }

  const getSearchData = (query: string) => {
    axios
      .get(`${searchApiURL}${query}`)
      .then((response: EasySoftwareSearchResponse) => {
        if (response.status === 200) {
          setData(response.data.items)
          setTotal(response.data.searchInformation.totalResults)
          setShowLoadMore(
            isThereMoreToLoad(response.data.searchInformation.totalResults)
          )
        }
        setLoading(false)
      })
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value.length > 2) {
      setLoading(true)
      delayedQuery(e.target.value)
    } else if (e.target.value.length === 0) {
      reset()
    }
  }

  const handleLoadMore = () => {
    setLoading(true)
    setShowLoadMore(false)
    setResultsIndex(resultsIndex + 10)
    axios
      .get(`${searchApiURL}${searchQuery}&start=${resultsIndex}`)
      .then((response: EasySoftwareSearchResponse) => {
        if (response.status === 200) {
          setData(concat(data, response.data.items))
          setShowLoadMore(isThereMoreToLoad())
        }
        setLoading(false)
      })
      .catch((error) => {
        console.log(error)
        setLoading(false)
        setShowLoadMore(isThereMoreToLoad())
      })
  }

  const isThereMoreToLoad = (totalCount?: number): boolean => {
    totalCount = totalCount ? totalCount : total
    return totalCount - Math.max(resultsIndex, 1) * 10 > 0
  }

  const reset = () => {
    setData([])
    setShowLoadMore(false)
    setTotal(0)
  }

  return (
    <div
      className={`${searchType}-search`}
      onMouseEnter={() => {
        props.setLanguageDialogOpen(false)
      }}
    >
      {searchType === `header` && (
        <div className={`${searchType}-search-icon`}>
          <PuxIconBuilder
            icon='SearchIcon'
            options={{
              color: {
                colorPrimary: `#0080ff`,
                colorSecondary: `#50596f`,
              },
              hoverColor: {
                colorPrimary: `#0080ff`,
                colorSecondary: `#50596f`,
              },
            }}
          />
        </div>
      )}
      <div className={`input-output-wrap${loading ? ` loading` : ``}`}>
        <PuxIconBuilder
          icon='SearchIcon'
          options={{
            color: {
              colorPrimary: `#0080ff`,
              colorSecondary: `#0080ff`,
            },
            hoverColor: {
              colorPrimary: `#0080ff`,
              colorSecondary: `#0080ff`,
            },
          }}
        />
        <input
          type='text'
          value={searchQuery}
          placeholder={
            searchType === `header`
              ? t`Redmine.Search.Placeholder`
              : t`Redmine.KnowledgeBase.Search.Placeholder`
          }
          onChange={(e) => handleSearch(e)}
        />
        <div className={`output${data?.length ? ` has-data` : ``}`}>
          {
            <div className='output-content'>
              {total && total > 0 ? (
                <div className='search-info'>
                  <strong>{total}</strong> {t`Redmine.Search.ResultsFound`}
                </div>
              ) : null}
              <ul>
                {data?.map((item, index) => (
                  <EasySoftwareSearchResult
                    {...item}
                    searchResultType={searchType}
                    key={index}
                  />
                ))}
              </ul>
              {showLoadMore ? (
                <div className='load-more-wrap'>
                  <span
                    className={
                      searchType === `header`
                        ? `pux-button pux-button-filled pux-button-primary`
                        : `knowledgebase-load-more`
                    }
                    onClick={() => handleLoadMore()}
                  >{t`Redmine.Search.LoadMore`}</span>
                </div>
              ) : null}
              {loading ? <div className='loader'></div> : null}
            </div>
          }
        </div>
      </div>
    </div>
  )
}

export default EasySoftwareSearch
